/**
 * Wizard State Machine Types
 *
 * Type definitions for the multi-step conversion wizard.
 *
 * Design principles:
 * - Immutable state updates
 * - Type-safe step transitions
 * - Serializable for persistence (future: localStorage)
 *
 * Session 4.1: Wizard State Machine + Steps 1-2
 */

import type { SubmissionValidationResult } from '@/types/validation';

// =============================================================================
// STEP DEFINITIONS
// =============================================================================

/**
 * Wizard step identifiers.
 */
export type WizardStep = 'source' | 'period' | 'validate' | 'generate';

/**
 * Ordered list of wizard steps.
 */
export const WIZARD_STEPS: WizardStep[] = [
  'source',
  'period',
  'validate',
  'generate',
];

/**
 * Configuration for each wizard step.
 */
export const STEP_CONFIG: Record<
  WizardStep,
  {
    index: number;
    label: string;
    description: string;
  }
> = {
  source: {
    index: 0,
    label: 'Select Source',
    description: 'Choose your payroll source',
  },
  period: {
    index: 1,
    label: 'Configure Period',
    description: 'Set contribution period and format',
  },
  validate: {
    index: 2,
    label: 'Review & Validate',
    description: 'Verify employee data',
  },
  generate: {
    index: 3,
    label: 'Generate C10',
    description: 'Download your file',
  },
};

// =============================================================================
// SOURCE TYPES
// =============================================================================

/**
 * Supported payroll source types.
 */
export type SourceType = 'quickbooks' | 'csv' | 'manual';

/**
 * Configuration for each source type.
 */
export interface SourceConfig {
  type: SourceType;
  label: string;
  description: string;
  icon: string;
  acceptedExtensions: string[];
}

/**
 * Source type configurations.
 */
export const SOURCE_CONFIGS: Record<SourceType, SourceConfig> = {
  quickbooks: {
    type: 'quickbooks',
    label: 'QuickBooks',
    description: 'Import from QuickBooks payroll export',
    icon: 'chart-bar',
    acceptedExtensions: ['.csv', '.xlsx', '.xls'],
  },
  csv: {
    type: 'csv',
    label: 'CSV File',
    description: 'Import from any CSV with column mapping',
    icon: 'document-text',
    acceptedExtensions: ['.csv'],
  },
  manual: {
    type: 'manual',
    label: 'Manual Entry',
    description: 'Enter employee data manually',
    icon: 'pencil',
    acceptedExtensions: [],
  },
};

// =============================================================================
// EMPLOYEE DATA
// =============================================================================

/**
 * Employee data for the wizard.
 * Parsed from source file or entered manually.
 */
export interface WizardEmployee {
  /** Temporary ID for UI (UUID) */
  id: string;
  /** NI number (8 digits) */
  employeeNi: string;
  /** Last name */
  lastName: string;
  /** First name */
  firstName: string;
  /** Middle initial (optional) */
  middleInitial?: string;
  /** Basic wages in dollars */
  basicWages: number;
  /** Gratuity wages in dollars (hospitality only) */
  gratuityWages: number;
  /** Number of weeks worked in the period */
  weeksWorked: number;
  /** Pay indicator (W=weekly, M=monthly) */
  payIndicator: 'W' | 'M';
  /** NIB category (1-6) */
  category: number;
  /** Date of birth (YYYY-MM-DD) */
  dateOfBirth?: string;
  /** Whether employee is retired */
  isRetired: boolean;

  // Metadata
  /** Row number from source file */
  sourceRow?: number;
  /** True if NI# was matched from employee roster */
  hasNiMatch: boolean;
  /** True if manually added (not from file) */
  isNew: boolean;
}

/**
 * Create a new empty employee for manual entry.
 */
export function createEmptyEmployee(id: string): WizardEmployee {
  return {
    id,
    employeeNi: '',
    lastName: '',
    firstName: '',
    basicWages: 0,
    gratuityWages: 0,
    weeksWorked: 4,
    payIndicator: 'W',
    category: 1,
    isRetired: false,
    hasNiMatch: false,
    isNew: true,
  };
}

// =============================================================================
// FORMAT TYPE
// =============================================================================

/**
 * C10 format types.
 */
export type FormatType = 'HOSPITALITY' | 'NON_HOSPITALITY';

// =============================================================================
// FILE UPLOAD STATE
// =============================================================================

/**
 * Uploaded file metadata.
 */
export interface UploadedFile {
  /** Original file object */
  file: File;
  /** File name */
  name: string;
  /** File size in bytes */
  size: number;
  /** Upload timestamp */
  uploadedAt: Date;
}

// =============================================================================
// PARSE RESULT
// =============================================================================

/**
 * Result from parsing an uploaded file.
 */
export interface ParseResult {
  /** Whether parsing succeeded */
  success: boolean;
  /** Detected source type */
  sourceType: SourceType;
  /** Detection confidence (0-1) */
  confidence: number;
  /** Parsed employees */
  employees: WizardEmployee[];
  /** Error messages if parsing failed */
  errors: string[];
  /** Warning messages */
  warnings: string[];
}

// =============================================================================
// GENERATED FILE
// =============================================================================

/**
 * Generated C10 file information.
 */
export interface GeneratedFile {
  /** Download URL */
  url: string;
  /** Suggested filename */
  filename: string;
  /** Generation timestamp */
  generatedAt: Date;
  /** Number of employees included */
  employeeCount: number;
  /** Total contributions in dollars */
  totalContributions: number;
  /** Filing record ID (for history) */
  filingId?: string;
}

// =============================================================================
// WIZARD STATE
// =============================================================================

/**
 * Complete wizard state.
 */
export interface WizardState {
  // Current step
  currentStep: WizardStep;

  // Step 1: Source
  sourceType: SourceType | null;
  uploadedFile: UploadedFile | null;
  parseResult: ParseResult | null;

  // Step 2: Period
  contributionMonth: number;
  contributionYear: number;
  formatType: FormatType;

  // Step 3: Validate
  employees: WizardEmployee[];
  validationResult: SubmissionValidationResult | null;
  acknowledgedWarnings: Set<string>;

  // Step 4: Generate
  generatedFile: GeneratedFile | null;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Completion tracking
  completedSteps: Set<WizardStep>;
}

/**
 * Create initial wizard state.
 */
export function createInitialState(): WizardState {
  const now = new Date();

  return {
    currentStep: 'source',
    sourceType: null,
    uploadedFile: null,
    parseResult: null,
    contributionMonth: now.getMonth() + 1, // Current month (1-12)
    contributionYear: now.getFullYear(),
    formatType: 'NON_HOSPITALITY',
    employees: [],
    validationResult: null,
    acknowledgedWarnings: new Set(),
    generatedFile: null,
    isLoading: false,
    error: null,
    completedSteps: new Set(),
  };
}

// =============================================================================
// ACTION TYPES
// =============================================================================

/**
 * All possible wizard actions.
 */
export type WizardAction =
  // Source actions
  | { type: 'SET_SOURCE_TYPE'; payload: SourceType }
  | { type: 'SET_UPLOADED_FILE'; payload: UploadedFile | null }
  | { type: 'SET_PARSE_RESULT'; payload: ParseResult }
  // Period actions
  | { type: 'SET_PERIOD'; payload: { month: number; year: number } }
  | { type: 'SET_FORMAT_TYPE'; payload: FormatType }
  // Employee actions
  | { type: 'SET_EMPLOYEES'; payload: WizardEmployee[] }
  | {
      type: 'UPDATE_EMPLOYEE';
      payload: { id: string; updates: Partial<WizardEmployee> };
    }
  | { type: 'ADD_EMPLOYEE'; payload: WizardEmployee }
  | { type: 'REMOVE_EMPLOYEE'; payload: string }
  // Validation actions
  | { type: 'SET_VALIDATION_RESULT'; payload: SubmissionValidationResult }
  | { type: 'ACKNOWLEDGE_WARNING'; payload: string }
  // Generate actions
  | { type: 'SET_GENERATED_FILE'; payload: GeneratedFile }
  // Navigation actions
  | { type: 'GO_TO_STEP'; payload: WizardStep }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  // UI actions
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET' };
